import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
  OnInit,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Store } from '@ngrx/store';
import { map, Observable, shareReplay, take } from 'rxjs';
import { UserState } from '@ups/user';
import {
  SearchVpVendorService,
  vendorGroupForCompany,
  vendorMatchesQuery,
  withVendorContactLookup,
} from '@ups/tool-tracker';
import { DynamicTypeaheadWebBaseComponent } from '../base/dynamic-typeahead.web-base-component';

const SEARCH_CONTROL_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DynamicTypeaheadVendorComponent),
  multi: true,
};

/** WI 34964: Viewpoint AP vendor search, scoped to the signed-in user's default company. */
@Component({
  selector: 'ups-dynamic-typeahead-vendor',
  templateUrl: 'dynamic-typeahead-vendor.component.html',
  providers: [SEARCH_CONTROL_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // eslint-disable-next-line @angular-eslint/prefer-standalone -- pre-commit-allow: declared in the dynamic-render NgModule with its typeahead siblings (WI 34964)
  standalone: false,
})
export class DynamicTypeaheadVendorComponent
  extends DynamicTypeaheadWebBaseComponent
  implements OnInit
{
  private readonly store = inject(Store);
  private readonly vendorService = inject(SearchVpVendorService);
  /** The user's default company as a vendor group; null (no usable company) lists nothing. */
  private readonly vendorGroup$: Observable<number | null> = this.store.pipe(
    UserState.selectCurrentTruthy(),
    take(1),
    map((myInfo) => vendorGroupForCompany(myInfo?.Data?.VPCompanyID)),
    shareReplay(1)
  );

  override ngOnInit(): void {
    this.queryFilter = vendorMatchesQuery;
    // valueProperty is pinned to 'vendor' by type in createConfigGroup (dynamicValuePropertyFor).
    // Searches wait for the vendor group (searchInGroup), so the reopen prefill run inside
    // super.ngOnInit cannot fail closed before the profile loads.
    this.searchQuery = (query: unknown) =>
      this.vendorService.searchInGroup(`${query ?? ''}`, this.vendorGroup$);
    // WI 34965: `updateOnValueChange` entries naming property 'contact' fetch the picked vendor's
    // Viewpoint contact; the search result itself has none. Same wiring as the NativeScript control.
    const options = this.config?.options;
    if (options?.updateOnValueChange?.length) {
      options.updateOnValueChange = withVendorContactLookup(
        options.updateOnValueChange,
        (group, vendor) => this.vendorService.contactFor(group, vendor)
      );
    }
    super.ngOnInit();
    // [value]-bound template: repaint on every control write (PR 4539 thread 56562).
    this.bindViewToControl();
  }

  /**
   * The shared base writes isLoading and searchError inside the search's subscribe, which does
   * not mark an OnPush view. Mark it once each search settles so the spinner and error repaint.
   */
  override filterChangeItem(
    ...args: Parameters<DynamicTypeaheadWebBaseComponent['filterChangeItem']>
  ) {
    return super
      .filterChangeItem(...args)
      .finally(() => this.cdr?.markForCheck());
  }
}
